import React from "react";
import PropTypes from "prop-types";
import "./PaginationControl.css";

/**
 * PaginationControl Component
 *
 * A professional, reusable pagination component that works with standard API responses
 * containing totalCount, currentPage, pageNumber, pageSize, and data fields.
 *
 * @component
 * @example
 * const apiResponse = {
 *   totalCount: 1000,
 *   currentPage: 1,
 *   pageNumber: 1,
 *   pageSize: 20,
 *   data: [...]
 * };
 *
 * <PaginationControl
 *   totalCount={apiResponse.totalCount}
 *   currentPage={apiResponse.currentPage}
 *   pageSize={apiResponse.pageSize}
 *   onPageChange={(page) => setPage(page)}
 *   onPageSizeChange={(size) => setPageSize(size)}
 *   pageSizeOptions={[20, 50, 100, 200]}
 * />
 */
const PaginationControl = ({
  totalCount = 0,
  currentPage = 1,
  pageSize = 20,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [20, 50, 100, 200],
  showPageSizeSelector = true,
  showPageInfo = true,
  showFirstLast = true,
  maxVisiblePages = 5,
  className = "",
  disabled = false,
}) => {
  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Calculate visible page range
  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);
    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (
      disabled ||
      newPage < 1 ||
      newPage > totalPages ||
      newPage === currentPage
    ) {
      return;
    }
    onPageChange?.(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (e) => {
    if (disabled) return;
    const newSize = parseInt(e.target.value, 10);
    onPageSizeChange?.(newSize);
  };

  // Calculate display range
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Don't render if no data
  if (totalCount === 0) {
    return (
      <div className={`pagination-control ${className}`}>
        <div className="pagination-info">No records found</div>
      </div>
    );
  }

  const visiblePages = getVisiblePages();

  return (
    <div
      className={`pagination-control ${className} ${disabled ? "disabled" : ""}`}
    >
      {/* Page Size Selector */}
      {showPageSizeSelector && (
        <div className="pagination-size-selector">
          <label htmlFor="pageSize">Rows per page:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={handlePageSizeChange}
            disabled={disabled}
            className="pagination-select"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Page Info */}
      {showPageInfo && (
        <div className="pagination-info">
          Showing {startItem.toLocaleString()} to {endItem.toLocaleString()} of{" "}
          {totalCount.toLocaleString()} entries
        </div>
      )}

      {/* Navigation Controls */}
      <div className="pagination-controls">
        {/* First Page Button */}
        {showFirstLast && (
          <button
            className="pagination-btn pagination-btn-first"
            onClick={() => handlePageChange(1)}
            disabled={disabled || currentPage === 1}
            title="First page"
            aria-label="Go to first page"
          >
            <span className="pagination-icon">«</span>
          </button>
        )}

        {/* Previous Page Button */}
        <button
          className="pagination-btn pagination-btn-prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={disabled || currentPage === 1}
          title="Previous page"
          aria-label="Go to previous page"
        >
          <span className="pagination-icon">‹</span>
        </button>

        {/* Page Number Buttons */}
        <div className="pagination-pages">
          {visiblePages.map((page) => (
            <button
              key={page}
              className={`pagination-btn pagination-btn-page ${
                page === currentPage ? "active" : ""
              }`}
              onClick={() => handlePageChange(page)}
              disabled={disabled}
              aria-label={`Go to page ${page}`}
              aria-current={page === currentPage ? "page" : undefined}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Next Page Button */}
        <button
          className="pagination-btn pagination-btn-next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={disabled || currentPage === totalPages}
          title="Next page"
          aria-label="Go to next page"
        >
          <span className="pagination-icon">›</span>
        </button>

        {/* Last Page Button */}
        {showFirstLast && (
          <button
            className="pagination-btn pagination-btn-last"
            onClick={() => handlePageChange(totalPages)}
            disabled={disabled || currentPage === totalPages}
            title="Last page"
            aria-label="Go to last page"
          >
            <span className="pagination-icon">»</span>
          </button>
        )}
      </div>
    </div>
  );
};

PaginationControl.propTypes = {
  /** Total number of items across all pages */
  totalCount: PropTypes.number.isRequired,
  /** Current active page number (1-indexed) */
  currentPage: PropTypes.number.isRequired,
  /** Number of items per page */
  pageSize: PropTypes.number.isRequired,
  /** Callback function when page changes - receives new page number */
  onPageChange: PropTypes.func.isRequired,
  /** Callback function when page size changes - receives new page size */
  onPageSizeChange: PropTypes.func,
  /** Array of available page size options */
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  /** Whether to show the page size selector dropdown */
  showPageSizeSelector: PropTypes.bool,
  /** Whether to show the page information text */
  showPageInfo: PropTypes.bool,
  /** Whether to show first and last page buttons */
  showFirstLast: PropTypes.bool,
  /** Maximum number of page buttons to show */
  maxVisiblePages: PropTypes.number,
  /** Additional CSS class names */
  className: PropTypes.string,
  /** Whether the pagination is disabled */
  disabled: PropTypes.bool,
};

export default PaginationControl;
